// src/layouts/MainLayout.js - Complete with Matching Permissions
import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  IconLayoutDashboard,
  IconUsers,
  IconMotorbike,
  IconCash,
  IconMessage,
  IconLogout,
  IconTicket,
  IconBell,
  IconMap,
  IconChartBar,
  IconDeviceAnalytics,
  IconAlertCircle,
  IconSettings,
  IconHistory,
  IconFileReport,
  IconUserShield,
} from '@tabler/icons-react';

function MainLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userName, setUserName] = useState('');
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const loadUser = () => {
      try {
        const adminData = localStorage.getItem('admin');
        console.log('📦 Admin data:', adminData);
        
        if (!adminData) {
          navigate('/login');
          return;
        }

        const user = JSON.parse(adminData);
        const role = user.role || user.role_name || 'admin';
        const name = user.full_name || user.name || 'Admin';
        
        console.log(`👤 User role: ${role}`);
        setUserRole(role);
        setUserName(name);
        setLoading(false);
      } catch (error) {
        console.error('Error loading user:', error);
        navigate('/login');
      }
    };

    loadUser();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate('/login');
  };

  // Get menu items based on role - MUST MATCH pagePermissions in App.js
  const getMenuItems = () => {
    // All possible menu items
    const allItems = [
      { path: '/', name: 'Overview', icon: <IconLayoutDashboard size={20} /> },
      { path: '/live-map', name: 'Live Map', icon: <IconMap size={20} /> },
      { path: '/drivers', name: 'Drivers', icon: <IconMotorbike size={20} /> },
      { path: '/driver-performance', name: 'Performance', icon: <IconChartBar size={20} /> },
      { path: '/customers', name: 'Customers', icon: <IconUsers size={20} /> },
      { path: '/rides', name: 'Rides', icon: <IconMotorbike size={20} /> },
      { path: '/analytics', name: 'Analytics', icon: <IconDeviceAnalytics size={20} /> },
      { path: '/revenue-analytics', name: 'Revenue', icon: <IconCash size={20} /> },
      { path: '/earnings', name: 'Earnings', icon: <IconCash size={20} /> },
      { path: '/vouchers', name: 'Vouchers', icon: <IconTicket size={20} /> },
      { path: '/notifications', name: 'Notifications', icon: <IconBell size={20} /> },
      { path: '/support', name: 'Support Chat', icon: <IconMessage size={20} /> },
      { path: '/users', name: 'User Management', icon: <IconUserShield size={20} /> },
      { path: '/audit', name: 'Audit Log', icon: <IconHistory size={20} /> },
      { path: '/reports', name: 'Reports', icon: <IconFileReport size={20} /> },
    ];

    // Define what each role can see - MUST MATCH App.js pagePermissions
    const roleMenus = {
      // Super Admin - Everything
      super_admin: allItems,
      
      // Admin - Everything EXCEPT user management and audit
      admin: allItems.filter(item => 
        !['/users', '/audit'].includes(item.path)
      ),
      
      // Finance - Overview, Revenue, Earnings, Vouchers, Reports, Customers
      finance: allItems.filter(item => 
        ['/', '/revenue-analytics', '/earnings', '/vouchers', '/reports', '/customers'].includes(item.path)
      ),
      
      // Support - Overview, Support, Customers, Rides, Drivers
      support: allItems.filter(item => 
        ['/', '/support', '/customers', '/rides', '/drivers'].includes(item.path)
      ),
      
      // Operations - Overview, Live Map, Drivers, Performance, Customers, Rides, Support
      operations: allItems.filter(item => 
        ['/', '/live-map', '/drivers', '/driver-performance', '/customers', '/rides', '/support'].includes(item.path)
      ),
      
      // Viewer - Overview, Analytics, Reports, Drivers, Customers, Rides
      viewer: allItems.filter(item => 
        ['/', '/analytics', '/reports', '/drivers', '/customers', '/rides'].includes(item.path)
      ),
    };

    // Default to admin if role not found
    const menu = roleMenus[userRole] || roleMenus.admin;
    console.log(`📋 Menu items for "${userRole}": ${menu.length}`);
    return menu;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>🔄</div>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Loading...</div>
        </div>
      </div>
    );
  }

  const menuItems = getMenuItems();

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <div style={{
        width: collapsed ? '80px' : '260px',
        backgroundColor: '#0f172a',
        color: 'white',
        transition: 'width 0.3s',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        zIndex: 1000,
        overflow: 'hidden',
      }}>
        {/* Logo */}
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          borderBottom: '1px solid #1e293b',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}>
          {!collapsed && (
            <>
              <span style={{ fontSize: '24px' }}>🚗</span>
              <h2 style={{ color: '#f97316', margin: 0, fontSize: '20px' }}>GoFurat</h2>
            </>
          )}
          {collapsed && <span style={{ fontSize: '24px' }}>🚗</span>}
        </div>
        
        {/* Collapse Button */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          style={{
            margin: '10px',
            padding: '8px',
            backgroundColor: '#1e293b',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            alignSelf: 'center',
            width: '40px',
          }}
        >
          {collapsed ? '→' : '←'}
        </button>

        {/* User Info */}
        {!collapsed && userRole && (
          <div style={{
            padding: '8px 16px',
            margin: '0 10px 10px 10px',
            backgroundColor: '#1e293b',
            borderRadius: '8px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '13px', color: '#94a3b8' }}>
              👤 {userName}
            </div>
            <div style={{ 
              fontSize: '11px', 
              color: userRole === 'super_admin' ? '#a78bfa' : '#f97316', 
              fontWeight: '600', 
              textTransform: 'uppercase',
              marginTop: '2px'
            }}>
              {userRole === 'super_admin' ? '👑 ' : ''}{userRole.replace('_', ' ')}
            </div>
          </div>
        )}

        {/* Menu Items */}
        <nav style={{ flex: 1, marginTop: '10px', overflowY: 'auto' }}>
          {menuItems.length === 0 ? (
            <div style={{ padding: '20px', color: '#64748b', textAlign: 'center', fontSize: '13px' }}>
              No menu items for this role
            </div>
          ) : (
            menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 20px',
                  color: location.pathname === item.path ? '#f97316' : '#94a3b8',
                  backgroundColor: location.pathname === item.path ? '#1e293b' : 'transparent',
                  textDecoration: 'none',
                  transition: 'all 0.2s',
                  borderLeft: location.pathname === item.path ? '3px solid #f97316' : '3px solid transparent',
                }}
              >
                {item.icon}
                {!collapsed && <span>{item.name}</span>}
              </Link>
            ))
          )}
        </nav>

        {/* Bottom - Logout */}
        <div style={{ padding: '16px', borderTop: '1px solid #1e293b' }}>
          <button
            onClick={handleLogout}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: '#ef4444',
              padding: '8px 12px',
              borderRadius: '8px',
              background: 'none',
              border: 'none',
              width: '100%',
              cursor: 'pointer',
              fontSize: '16px',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <IconLogout size={20} />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ 
        flex: 1, 
        backgroundColor: '#f1f5f9',
        marginLeft: collapsed ? '80px' : '260px',
        transition: 'margin-left 0.3s',
        minHeight: '100vh',
      }}>
        <div style={{ padding: '24px' }}>
          <Outlet />
        </div>
      </div>
    </div>
  );
}

export default MainLayout;